import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Pencil, X, Check, Bookmark, List, Map as MapIcon, SlidersHorizontal, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import MapView from "@/components/MapView";
import MapListCard from "@/components/MapListCard";
import { useSeo } from "@/lib/seo";

const DEFAULT_CENTER = [45.4642, 9.19];
const DEFAULT_ZOOM = 12;

function readTheme() {
    if (typeof document === "undefined") return "dark";
    return document.documentElement.getAttribute("data-theme") === "light" ? "light" : "dark";
}

export default function MapCatalog() {
    const { user } = useAuth();
    const navigate = useNavigate();
    useSeo({
        title: "Mappa immobili in vendita e affitto | VendiDirect",
        description: "Cerca casa sulla mappa interattiva: disegna la tua zona, filtra per prezzo e tipologia e scopri appartamenti, ville e attici disponibili in tutta Italia.",
        canonical: "/annunci/mappa",
    });
    const [searchParams, setSearchParams] = useSearchParams();
    // Filter state (subset of Listings.jsx)
    const [filters, setFilters] = useState({
        city: searchParams.get("city") || "",
        property_type: searchParams.get("property_type") || "",
        transaction_type: searchParams.get("transaction_type") || "",
        min_price: searchParams.get("min_price") || "",
        max_price: searchParams.get("max_price") || "",
        min_rooms: searchParams.get("min_rooms") || "",
    });
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [polygon, setPolygon] = useState(null); // [[lat,lng],...]
    const [drawing, setDrawing] = useState(false);
    const [activeId, setActiveId] = useState(null);
    const [searchOnMove, setSearchOnMove] = useState(true);
    const [viewport, setViewport] = useState(null);
    const [mobileView, setMobileView] = useState("map"); // "list" | "map"
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [saveName, setSaveName] = useState("");
    const [saving, setSaving] = useState(false);
    const [theme, setTheme] = useState(readTheme());
    const mapWrapperRef = useRef(null);

    // React to theme toggle globally (data-theme attribute mutation)
    useEffect(() => {
        const target = document.documentElement;
        const obs = new MutationObserver(() => setTheme(readTheme()));
        obs.observe(target, { attributes: true, attributeFilter: ["data-theme"] });
        return () => obs.disconnect();
    }, []);

    // ---- Fetch properties ----
    const fetchItems = useCallback(async ({ bbox, poly, override }) => {
        setLoading(true);
        try {
            const params = { limit: 100, only_geo: 1 };
            const f = override || filters;
            if (f.city) params.city = f.city;
            if (f.property_type) params.property_type = f.property_type;
            if (f.transaction_type) params.transaction_type = f.transaction_type;
            if (f.min_price) params.min_price = f.min_price;
            if (f.max_price) params.max_price = f.max_price;
            if (f.min_rooms) params.min_rooms = f.min_rooms;
            if (poly && poly.length >= 3) {
                params.polygon = JSON.stringify(poly);
            } else if (bbox) {
                params.bbox = bbox.join(",");
            }
            const res = await api.get("/properties", { params });
            setItems(res.data.items || []);
        } catch (e) {
            console.error("[MapCatalog] fetch error", e);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    // Store latest fetchItems in a ref to allow a truly-once mount effect
    const fetchItemsRef = useRef(fetchItems);
    useEffect(() => { fetchItemsRef.current = fetchItems; }, [fetchItems]);

    // Initial mount: broad fetch + one-time geocode backfill (does NOT depend on
    // filters so it does not re-fire on every filter change).
    useEffect(() => {
        api.post("/properties/geo/backfill", null, { params: { limit: 5 } })
            .catch(() => {})
            .finally(() => fetchItemsRef.current({}));
    }, []);

    // When polygon becomes non-null → always refetch inside polygon
    useEffect(() => {
        if (polygon && polygon.length >= 3) {
            fetchItemsRef.current({ poly: polygon });
        }
    }, [polygon]);

    // Search-as-I-move (only when NO polygon)
    const onViewportChange = useCallback((vp) => {
        setViewport(vp);
        if (!searchOnMove) return;
        if (polygon && polygon.length >= 3) return; // polygon takes precedence
        fetchItems({ bbox: vp.bbox });
    }, [searchOnMove, polygon, fetchItems]);

    const onFilterChange = (patch) => {
        const nf = { ...filters, ...patch };
        setFilters(nf);
        const sp = {};
        Object.entries(nf).forEach(([k, v]) => { if (v) sp[k] = v; });
        setSearchParams(sp, { replace: true });
        fetchItems({ bbox: viewport?.bbox, poly: polygon, override: nf });
    };

    // ---- Drawing toolbar handlers ----
    const beginDrawing = () => {
        // Clear existing polygon state (no CustomEvent — that would call
        // onPolygonComplete(null) synchronously and batch-cancel setDrawing(true)).
        // The MapView useEffect on drawingEnabled will clear any stale draft.
        setPolygon(null);
        setDrawing(true);
    };

    // Refs so imperative handlers see current state (avoid stale closures)
    const drawingRef = useRef(drawing);
    useEffect(() => { drawingRef.current = drawing; }, [drawing]);
    const polygonRef = useRef(polygon);
    useEffect(() => { polygonRef.current = polygon; }, [polygon]);

    const finishDrawing = () => {
        const el = document.querySelector('[data-testid="map-catalog-map"]');
        if (el) el.dispatchEvent(new CustomEvent("vd:finish-polygon"));
        // If MapView didn't close (needs >=3 pts), the polygon state won't update.
        // Give the user a hint after a tick so they know why.
        setTimeout(() => {
            if (drawingRef.current && !polygonRef.current) {
                toast.info("Aggiungi almeno 3 punti per chiudere la zona");
            }
        }, 260);
    };

    const cancelDrawing = () => {
        setDrawing(false);
        const el = document.querySelector('[data-testid="map-catalog-map"]');
        if (el) el.dispatchEvent(new CustomEvent("vd:clear-draft"));
    };

    const deletePolygon = () => {
        setPolygon(null);
        setDrawing(false);
        const el = document.querySelector('[data-testid="map-catalog-map"]');
        if (el) el.dispatchEvent(new CustomEvent("vd:clear-draft"));
        // Fetch again without polygon
        fetchItems({ bbox: viewport?.bbox });
    };

    const onPolygonComplete = useCallback((coords) => {
        // Only close drawing mode when a valid polygon (>=3 pts) is received
        if (coords && coords.length >= 3) {
            setDrawing(false);
            setPolygon(coords);
        }
    }, []);

    const onSaveZone = () => {
        if (!user) {
            toast.info("Accedi per salvare la zona e ricevere alert email");
            navigate("/login");
            return;
        }
        if (!polygon || polygon.length < 3) return;
        setSaveName(filters.city ? `Zona ${filters.city}` : "La mia zona");
        setShowSaveModal(true);
    };

    const confirmSaveZone = async () => {
        if (!saveName.trim()) return;
        setSaving(true);
        try {
            const payload = { name: saveName.trim(), polygon };
            if (filters.city) payload.city = filters.city;
            if (filters.property_type) payload.property_type = filters.property_type;
            if (filters.transaction_type) payload.transaction_type = filters.transaction_type;
            if (filters.min_price) payload.min_price = parseFloat(filters.min_price);
            if (filters.max_price) payload.max_price = parseFloat(filters.max_price);
            if (filters.min_rooms) payload.min_rooms = parseInt(filters.min_rooms, 10);
            await api.post("/saved-searches", payload);
            toast.success("Zona salvata! Riceverai un'email quando esce un nuovo immobile nell'area.");
            setShowSaveModal(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore nel salvataggio della zona");
        } finally {
            setSaving(false);
        }
    };

    const initialCenter = useMemo(() => DEFAULT_CENTER, []);

    const hasPolygon = polygon && polygon.length >= 3;

    return (
        <div data-testid="map-catalog-page" className="pt-20">
            {/* Mobile view toggle */}
            <div className="lg:hidden sticky top-16 z-40 bg-ink-950/90 backdrop-blur border-b border-white/10 px-4 py-2 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <SlidersHorizontal size={14} className="text-gold" />
                    <span className="label-eyebrow text-xs">Catalogo · Mappa</span>
                </div>
                <div className="flex bg-white/5 border border-white/10 rounded overflow-hidden">
                    <button
                        data-testid="mobile-view-list"
                        onClick={() => setMobileView("list")}
                        className={`px-3 py-1.5 text-xs flex items-center gap-1 ${mobileView === "list" ? "bg-gold text-black" : "text-ink-400"}`}
                    ><List size={12} /> Lista</button>
                    <button
                        data-testid="mobile-view-map"
                        onClick={() => setMobileView("map")}
                        className={`px-3 py-1.5 text-xs flex items-center gap-1 ${mobileView === "map" ? "bg-gold text-black" : "text-ink-400"}`}
                    ><MapIcon size={12} /> Mappa</button>
                </div>
            </div>

            <div className="flex flex-col lg:flex-row min-h-[calc(100vh-5rem)]">
                {/* LEFT: filters + list */}
                <aside
                    className={`lg:w-1/2 lg:border-r border-white/10 lg:sticky lg:top-20 lg:h-[calc(100vh-5rem)] flex flex-col ${mobileView === "list" ? "flex" : "hidden lg:flex"}`}
                    data-testid="map-catalog-sidebar"
                >
                    {/* Filters bar */}
                    <div className="border-b border-white/10 p-4 space-y-3">
                        <div className="grid grid-cols-2 gap-2">
                            <input
                                data-testid="map-filter-city"
                                value={filters.city}
                                onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                                onBlur={() => onFilterChange({})}
                                placeholder="Città"
                                className="bg-transparent border border-white/10 px-3 py-2 text-sm text-ink-50 focus:border-gold outline-none"
                            />
                            <select
                                data-testid="map-filter-transaction"
                                value={filters.transaction_type}
                                onChange={(e) => onFilterChange({ transaction_type: e.target.value })}
                                className="bg-transparent border border-white/10 px-3 py-2 text-sm text-ink-50 focus:border-gold outline-none"
                            >
                                <option value="" className="bg-ink-950">Vendita e Affitto</option>
                                <option value="vendita" className="bg-ink-950">Vendita</option>
                                <option value="affitto" className="bg-ink-950">Affitto</option>
                            </select>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                            <input
                                data-testid="map-filter-min-price"
                                type="number"
                                value={filters.min_price}
                                onChange={(e) => setFilters({ ...filters, min_price: e.target.value })}
                                onBlur={() => onFilterChange({})}
                                placeholder="€ min"
                                className="bg-transparent border border-white/10 px-3 py-2 text-sm text-ink-50 focus:border-gold outline-none"
                            />
                            <input
                                data-testid="map-filter-max-price"
                                type="number"
                                value={filters.max_price}
                                onChange={(e) => setFilters({ ...filters, max_price: e.target.value })}
                                onBlur={() => onFilterChange({})}
                                placeholder="€ max"
                                className="bg-transparent border border-white/10 px-3 py-2 text-sm text-ink-50 focus:border-gold outline-none"
                            />
                            <input
                                data-testid="map-filter-min-rooms"
                                type="number"
                                value={filters.min_rooms}
                                onChange={(e) => setFilters({ ...filters, min_rooms: e.target.value })}
                                onBlur={() => onFilterChange({})}
                                placeholder="Locali"
                                className="bg-transparent border border-white/10 px-3 py-2 text-sm text-ink-50 focus:border-gold outline-none"
                            />
                        </div>
                        <div className="flex items-center justify-between gap-3 text-xs pt-1">
                            <label className="flex items-center gap-2 text-ink-400 cursor-pointer" data-testid="map-search-on-move-label">
                                <input
                                    type="checkbox"
                                    checked={searchOnMove}
                                    onChange={(e) => setSearchOnMove(e.target.checked)}
                                    className="accent-gold"
                                    data-testid="map-search-on-move"
                                    disabled={hasPolygon}
                                />
                                Cerca mentre muovo la mappa
                            </label>
                            <button
                                data-testid="map-view-toggle-list"
                                onClick={() => {
                                    const sp = new URLSearchParams();
                                    Object.entries(filters).forEach(([k, v]) => { if (v) sp.set(k, v); });
                                    const qs = sp.toString();
                                    navigate(`/annunci${qs ? "?" + qs : ""}`);
                                }}
                                className="vd-classic-view-btn"
                            >
                                <ArrowLeft size={13} /> Torna alla lista
                            </button>
                        </div>
                    </div>
                    {/* Results */}
                    <div className="flex-1 overflow-y-auto p-3">
                        <div className="flex items-center justify-between mb-3 px-1">
                            <span className="label-eyebrow text-[10px]" data-testid="map-results-count">
                                {loading
                                    ? "Ricerca…"
                                    : `${items.length} ${items.length === 1 ? "risultato" : "risultati"}${hasPolygon ? " in zona" : ""}`}
                            </span>
                            {hasPolygon && (
                                <button
                                    data-testid="map-list-clear-polygon"
                                    onClick={deletePolygon}
                                    className="text-xs text-ink-400 hover:text-gold flex items-center gap-1"
                                >
                                    <X size={12} /> Rimuovi zona
                                </button>
                            )}
                        </div>
                        {!loading && items.length === 0 && (
                            <div className="text-center text-ink-400 text-sm py-16 border border-white/10">
                                Nessun immobile con coordinate in questa area.<br />
                                <span className="text-xs opacity-60">Prova ad allargare la mappa o rimuovere la zona.</span>
                            </div>
                        )}
                        <div className="space-y-2">
                            {items.map((p) => (
                                <MapListCard
                                    key={p.id}
                                    property={p}
                                    isActive={p.id === activeId}
                                    onHover={setActiveId}
                                    onLeave={() => setActiveId(null)}
                                />
                            ))}
                        </div>
                    </div>
                </aside>

                {/* RIGHT: map */}
                <div
                    ref={mapWrapperRef}
                    className={`relative lg:w-1/2 lg:sticky lg:top-20 lg:h-[calc(100vh-5rem)] h-[calc(100vh-8rem)] ${mobileView === "map" ? "block" : "hidden lg:block"}`}
                    data-testid="map-catalog-mapwrap"
                >
                    <MapView
                        properties={items}
                        center={initialCenter}
                        zoom={DEFAULT_ZOOM}
                        activeId={activeId}
                        drawingEnabled={drawing}
                        polygon={polygon}
                        onPolygonComplete={onPolygonComplete}
                        onViewportChange={onViewportChange}
                        onPinClick={setActiveId}
                        theme={theme}
                    />
                    {/* Drawing toolbar */}
                    <div className="vd-draw-toolbar" data-testid="map-draw-toolbar">
                        {!drawing && !hasPolygon && (
                            <button
                                data-testid="map-draw-start"
                                onClick={beginDrawing}
                                title="Disegna una zona"
                                className="vd-draw-btn"
                            >
                                <Pencil size={16} />
                            </button>
                        )}
                        {drawing && (
                            <>
                                <button
                                    data-testid="map-draw-finish"
                                    onClick={finishDrawing}
                                    title="Chiudi il poligono"
                                    className="vd-draw-btn is-active"
                                >
                                    <Check size={16} />
                                </button>
                                <button
                                    data-testid="map-draw-cancel"
                                    onClick={cancelDrawing}
                                    title="Annulla"
                                    className="vd-draw-btn"
                                >
                                    <X size={16} />
                                </button>
                            </>
                        )}
                        {!drawing && hasPolygon && (
                            <button
                                data-testid="map-draw-delete"
                                onClick={deletePolygon}
                                title="Cancella zona"
                                className="vd-draw-btn"
                            >
                                <X size={16} />
                            </button>
                        )}
                    </div>

                    {/* Drawing hint */}
                    {drawing && (
                        <div className="vd-move-hint" data-testid="map-drawing-hint">
                            <Pencil size={12} />
                            Tocca la mappa per aggiungere punti · <button onClick={finishDrawing}>Chiudi</button>
                        </div>
                    )}

                    {/* Save zone CTA */}
                    {hasPolygon && !drawing && (
                        <button
                            data-testid="map-save-zone"
                            onClick={onSaveZone}
                            className="vd-save-zone"
                        >
                            <Bookmark size={14} /> Salva questa zona e avvisami
                        </button>
                    )}
                    {loading && (
                        <div className="vd-map-overlay">
                            <div>Aggiornamento risultati…</div>
                        </div>
                    )}
                </div>
            </div>

            {/* Save Zone Modal */}
            {showSaveModal && (
                <div
                    data-testid="save-zone-modal"
                    className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-6"
                    onClick={() => setShowSaveModal(false)}
                >
                    <div className="bg-ink-950 border border-gold/40 p-8 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <span className="label-eyebrow">Alert zona</span>
                            <button onClick={() => setShowSaveModal(false)} className="text-ink-400 hover:text-gold"><X size={18} /></button>
                        </div>
                        <h3 className="font-serif text-2xl text-ink-50 mb-3">Ricevi email per questa area</h3>
                        <p className="text-sm text-ink-400 mb-5">
                            Ti avviseremo appena un nuovo annuncio verrà pubblicato all&apos;interno della zona che hai disegnato.
                            Vertici: <span className="text-gold">{polygon?.length || 0}</span>.
                        </p>
                        <input
                            data-testid="save-zone-name"
                            value={saveName}
                            onChange={(e) => setSaveName(e.target.value)}
                            placeholder="Nome della zona (es. Milano Centro)"
                            className="w-full bg-transparent border border-white/15 px-4 py-3 text-ink-50 focus:border-gold outline-none mb-4"
                        />
                        <button
                            data-testid="save-zone-confirm"
                            disabled={saving || !saveName.trim()}
                            onClick={confirmSaveZone}
                            className="w-full bg-gold text-black py-3 text-sm font-medium hover:bg-gold-soft transition-colors disabled:opacity-50"
                        >
                            {saving ? "Salvataggio…" : "Attiva alert email"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
